import { useState, useCallback } from "react";
import { useTranslation } from "@/contexts/LanguageContext";
import { Trophy, ArrowRight, ArrowCounterClockwise } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";

const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

export const questions = {
  en: [
    {
      q: "What does a WIDER aperture (lower f-number) do?",
      opts: ["Deeper depth of field", "Shallower depth of field", "Slower shutter speed", "Higher ISO"],
      correct: 1,
      explain: "A wider aperture (e.g., f/1.8) lets in more light and creates a shallow depth of field, blurring the background.",
    },
    {
      q: "Which shutter speed would FREEZE a fast-moving athlete?",
      opts: ["1/15 sec", "1/60 sec", "1/500 sec", "1/2000 sec"],
      correct: 3,
      explain: "1/2000 sec is fast enough to freeze most sports action without motion blur.",
    },
    {
      q: "What happens when you increase ISO?",
      opts: ["Image gets darker", "Image gets brighter but noisier", "Depth of field changes", "Shutter speed slows"],
      correct: 1,
      explain: "Higher ISO increases the sensor's sensitivity, making the image brighter but introducing more digital noise/grain.",
    },
    {
      q: "In the Rule of Thirds, where should you place the subject?",
      opts: ["Dead center", "Along grid lines or intersections", "In the bottom-left corner", "It doesn't matter"],
      correct: 1,
      explain: "Placing key elements along the imaginary thirds grid lines or at their intersections creates a more dynamic, balanced composition.",
    },
    {
      q: "What is 'Golden Hour' in photography?",
      opts: ["Noon sunlight", "First and last hour of sunlight", "Midnight", "Any time with flash"],
      correct: 1,
      explain: "Golden Hour occurs during the first and last hour of sunlight — producing warm, soft, directional light that flatters most subjects.",
    },
    {
      q: "Which focus mode is best for photographing a running dog?",
      opts: ["AF-S (Single)", "AF-C (Continuous)", "Manual Focus", "Auto-Area"],
      correct: 1,
      explain: "AF-C (Continuous autofocus) continuously tracks moving subjects, keeping them in focus as they move through the frame.",
    },
    {
      q: "What does the 'Sunny 16 Rule' state?",
      opts: [
        "Always use f/16 indoors",
        "On a sunny day at f/16, use shutter speed = 1/ISO",
        "ISO should always be 1600 outdoors",
        "Use 16mm lens in sunlight",
      ],
      correct: 1,
      explain: "The Sunny 16 Rule says: on a bright sunny day at f/16, your shutter speed should be the reciprocal of your ISO (e.g., ISO 100 → 1/100 sec).",
    },
    {
      q: "What does 'Spot Metering' measure?",
      opts: ["The entire scene average", "A small area around the focus point", "Only the brightest part", "The corners of the frame"],
      correct: 1,
      explain: "Spot metering reads exposure from a very small area (typically 2-5% of the frame), useful for high-contrast scenes where you need precise metering on your subject.",
    },
    {
      q: "Which white balance preset adds warmth to an overcast scene?",
      opts: ["Tungsten", "Fluorescent", "Cloudy", "Auto"],
      correct: 2,
      explain: "The 'Cloudy' preset adds slight warmth (raises Kelvin) to compensate for the cool, blue-ish light of overcast conditions.",
    },
    {
      q: "What is 'leading lines' in composition?",
      opts: [
        "Lines drawn on the image in post-processing",
        "Natural lines that guide the viewer's eye toward the subject",
        "Lines created by motion blur",
        "The grid lines on your viewfinder",
      ],
      correct: 1,
      explain: "Leading lines are visual lines (roads, fences, rivers, edges) that naturally draw the viewer's eye into the image and toward the main subject.",
    },
    {
      q: "When should you sharpen an image in post-processing?",
      opts: ["First, before all other edits", "After cropping and exposure adjustments", "Only for prints", "Sharpening is never needed"],
      correct: 1,
      explain: "Sharpening should be one of the last steps — after you've cropped, adjusted exposure, contrast, and color. Applying it early can amplify noise.",
    },
    {
      q: "What does a crop factor of 1.6x mean?",
      opts: [
        "The lens is 1.6x larger",
        "The field of view is 1.6x narrower than full-frame",
        "The ISO is 1.6x more sensitive",
        "The shutter is 1.6x faster",
      ],
      correct: 1,
      explain: "A 1.6x crop factor means the sensor is smaller than full-frame, giving a 1.6x narrower field of view. A 50mm lens behaves like 80mm equivalent.",
    },
    {
      q: "You are photographing a white egret against a dark forest. Which metering mode will give you the most accurate exposure on the bird's white feathers?",
      opts: ["Matrix / Evaluative (whole scene)", "Center-Weighted", "Spot Metering on the bird", "Partial Metering on the trees"],
      correct: 2,
      explain: "Spot Metering reads only ~1.5-3% of the frame. Pointed at the bright white bird, it tells the camera to expose for the bird (not the dark background) — preserving feather detail and avoiding blown highlights.",
    },
    {
      q: "What is the main reason professional photographers configure 'Back-Button Focus' (decoupling AF from the shutter button)?",
      opts: [
        "To save battery life",
        "To focus once, then recompose and shoot bursts without the camera trying to refocus",
        "To make autofocus faster than manual focus",
        "It is required for HDR bracketing",
      ],
      correct: 1,
      explain: "BBF moves the AF trigger to a dedicated rear button (AF-ON). The shutter button only takes the photo — letting you focus once, recompose freely, and fire bursts without the camera re-focusing on the wrong subject at the moment of capture.",
    },
    {
      q: "True or False: It is safe to clean your camera sensor with a can of compressed air.",
      opts: ["True — it's the fastest method", "True, as long as you hold the can upright", "False — propellant can spray onto the sensor and the pressure pushes dust deeper", "False — only because it's expensive"],
      correct: 2,
      explain: "FALSE. Canned air contains liquid propellant that leaves permanent residue if it sprays onto the sensor, and its pressure can drive dust deeper into the sensor stack. Use a manual rocket blower instead, never canned air.",
    },
    {
      q: "You are shooting a snowy mountain landscape. Your camera's meter exposes the scene at 0 EV and the snow comes out gray. What should you do?",
      opts: ["Decrease exposure compensation to -1 EV", "Increase exposure compensation to +1 to +1.7 EV", "Switch to spot metering on the gray snow", "Decrease ISO until the snow looks white"],
      correct: 1,
      explain: "The meter wants everything to average 18% gray, which turns white snow into gray slush. Add +1 to +1.7 EV of exposure compensation to overexpose intentionally and render the snow as actual white.",
    },
    {
      q: "True or False: A 200mm telephoto lens 'compresses' the background, making mountains behind your subject look bigger and closer.",
      opts: [
        "True — telephoto glass physically compresses depth",
        "True — only with full-frame sensors",
        "False — the LENS does nothing; the camera-to-subject DISTANCE creates that perspective. The telephoto just lets you stand far away.",
        "False — compression only happens with macro lenses",
      ],
      correct: 2,
      explain: "Perspective compression is created by DISTANCE, not focal length. A 200mm and a 50mm shot from the same spot, then cropped to match, look identical. The telephoto's job is to let you stand far away while keeping the subject framed — and standing far is what makes the mountains look big and stacked behind.",
    },
  ],
  es: [
    {
      q: "Que hace una apertura MAS ABIERTA (numero f menor)?",
      opts: ["Mayor profundidad de campo", "Menor profundidad de campo", "Velocidad mas lenta", "ISO mas alto"],
      correct: 1,
      explain: "Una apertura mas abierta (ej. f/1.8) deja entrar mas luz y crea una profundidad de campo reducida, difuminando el fondo.",
    },
    {
      q: "Que velocidad de obturador CONGELARIA a un atleta en movimiento?",
      opts: ["1/15 seg", "1/60 seg", "1/500 seg", "1/2000 seg"],
      correct: 3,
      explain: "1/2000 seg es suficientemente rapida para congelar la mayoria de la accion deportiva sin desenfoque de movimiento.",
    },
    {
      q: "Que pasa cuando aumentas el ISO?",
      opts: ["La imagen se oscurece", "La imagen se aclara pero con mas ruido", "Cambia la profundidad de campo", "La velocidad se reduce"],
      correct: 1,
      explain: "Un ISO mas alto aumenta la sensibilidad del sensor, haciendo la imagen mas brillante pero introduciendo mas ruido digital.",
    },
    {
      q: "En la Regla de los Tercios, donde debes colocar el sujeto?",
      opts: ["En el centro exacto", "A lo largo de las lineas o intersecciones", "En la esquina inferior izquierda", "No importa"],
      correct: 1,
      explain: "Colocar elementos clave a lo largo de las lineas de tercios o en sus intersecciones crea una composicion mas dinamica y equilibrada.",
    },
    {
      q: "Que es la 'Hora Dorada' en fotografia?",
      opts: ["Luz del mediodia", "Primera y ultima hora de sol", "Medianoche", "Cualquier momento con flash"],
      correct: 1,
      explain: "La Hora Dorada ocurre durante la primera y ultima hora de luz solar — produciendo luz calida, suave y direccional que favorece a la mayoria de sujetos.",
    },
    {
      q: "Que modo de enfoque es mejor para fotografiar un perro corriendo?",
      opts: ["AF-S (Unico)", "AF-C (Continuo)", "Enfoque Manual", "Area Automatica"],
      correct: 1,
      explain: "AF-C (enfoque continuo) rastrea continuamente sujetos en movimiento, manteniendolos enfocados mientras se mueven por el encuadre.",
    },
    {
      q: "Que establece la 'Regla Sunny 16'?",
      opts: [
        "Siempre usar f/16 en interiores",
        "En un dia soleado a f/16, la velocidad = 1/ISO",
        "El ISO siempre debe ser 1600 en exteriores",
        "Usar lente de 16mm bajo el sol",
      ],
      correct: 1,
      explain: "La Regla Sunny 16 dice: en un dia soleado brillante a f/16, tu velocidad debe ser el reciproco del ISO (ej. ISO 100 → 1/100 seg).",
    },
    {
      q: "Que mide la 'Medicion Puntual'?",
      opts: ["El promedio de toda la escena", "Una pequena area alrededor del punto de enfoque", "Solo la parte mas brillante", "Las esquinas del encuadre"],
      correct: 1,
      explain: "La medicion puntual lee la exposicion de un area muy pequena (2-5% del encuadre), util para escenas de alto contraste donde necesitas medicion precisa.",
    },
    {
      q: "Que preajuste de balance de blancos anade calidez a una escena nublada?",
      opts: ["Tungsteno", "Fluorescente", "Nublado", "Auto"],
      correct: 2,
      explain: "El preajuste 'Nublado' anade ligera calidez (sube Kelvin) para compensar la luz fria y azulada de condiciones nubladas.",
    },
    {
      q: "Que son las 'lineas guia' en composicion?",
      opts: [
        "Lineas dibujadas en la imagen en post-procesado",
        "Lineas naturales que guian la mirada hacia el sujeto",
        "Lineas creadas por desenfoque de movimiento",
        "Las lineas de cuadricula en tu visor",
      ],
      correct: 1,
      explain: "Las lineas guia son lineas visuales (caminos, cercas, rios, bordes) que atraen naturalmente la mirada del espectador hacia el sujeto principal.",
    },
    {
      q: "Cuando debes enfocar (sharpening) una imagen en post-procesado?",
      opts: ["Primero, antes de todo", "Despues de recortar y ajustar exposicion", "Solo para impresiones", "Nunca se necesita"],
      correct: 1,
      explain: "El enfoque debe ser uno de los ultimos pasos — despues de recortar, ajustar exposicion, contraste y color. Aplicarlo antes puede amplificar el ruido.",
    },
    {
      q: "Que significa un factor de recorte de 1.6x?",
      opts: [
        "El lente es 1.6x mas grande",
        "El campo de vision es 1.6x mas estrecho que full-frame",
        "El ISO es 1.6x mas sensible",
        "La velocidad es 1.6x mas rapida",
      ],
      correct: 1,
      explain: "Un factor de recorte 1.6x significa que el sensor es mas pequeno que full-frame, dando un campo de vision 1.6x mas estrecho. Un lente 50mm se comporta como 80mm equivalente.",
    },
    {
      q: "Estas fotografiando una garza blanca contra un bosque oscuro. Que modo de medicion te dara la exposicion mas precisa sobre las plumas blancas del ave?",
      opts: ["Matrix / Evaluativa (toda la escena)", "Promedio Central", "Medicion Puntual (Spot) sobre el ave", "Medicion Parcial sobre los arboles"],
      correct: 2,
      explain: "La Medicion Puntual lee solo ~1.5-3% del encuadre. Apuntada al ave blanca brillante, le dice a la camara que exponga para el ave (no para el fondo oscuro) — preservando el detalle de las plumas y evitando altas luces quemadas.",
    },
    {
      q: "Cual es la razon principal por la que los fotografos profesionales configuran 'Back-Button Focus' (desacoplar el AF del disparador)?",
      opts: [
        "Para ahorrar bateria",
        "Para enfocar una vez, recomponer y disparar rafagas sin que la camara intente re-enfocar",
        "Para hacer el autoenfoque mas rapido que el manual",
        "Es obligatorio para bracketing HDR",
      ],
      correct: 1,
      explain: "El BBF mueve el AF a un boton trasero dedicado (AF-ON). El disparador solo toma la foto — permitiendote enfocar una vez, recomponer libremente, y disparar rafagas sin que la camara re-enfoque sobre el sujeto equivocado al momento de la captura.",
    },
    {
      q: "Verdadero o Falso: Es seguro limpiar el sensor de tu camara con una lata de aire comprimido.",
      opts: ["Verdadero — es el metodo mas rapido", "Verdadero, mientras sostengas la lata vertical", "Falso — el propelente puede rociar al sensor y la presion empuja el polvo mas adentro", "Falso — solo porque es caro"],
      correct: 2,
      explain: "FALSO. Las latas de aire comprimido contienen propelente liquido que deja residuo permanente si rocia el sensor, y su presion puede empujar el polvo mas profundamente. Usa un soplador manual de cohete (rocket blower), nunca aire enlatado.",
    },
    {
      q: "Estas fotografiando un paisaje nevado de montaña. El medidor de tu camara expone la escena a 0 EV y la nieve sale gris. Que debes hacer?",
      opts: ["Bajar la compensacion a -1 EV", "Subir la compensacion de exposicion a +1 a +1.7 EV", "Cambiar a medicion puntual sobre la nieve gris", "Bajar el ISO hasta que la nieve se vea blanca"],
      correct: 1,
      explain: "El medidor quiere que todo promedie gris medio al 18%, lo que convierte la nieve blanca en lodo gris. Suma +1 a +1.7 EV de compensacion de exposicion para sobreexponer intencionalmente y que la nieve aparezca realmente blanca.",
    },
    {
      q: "Verdadero o Falso: Un teleobjetivo de 200mm 'comprime' el fondo, haciendo que las montañas detras del sujeto se vean mas grandes y mas cercanas.",
      opts: [
        "Verdadero — el vidrio del telefoto comprime fisicamente la profundidad",
        "Verdadero — solo con sensores full-frame",
        "Falso — el LENTE no hace nada; la DISTANCIA camara-sujeto crea esa perspectiva. El telefoto solo te permite estar lejos.",
        "Falso — la compresion solo ocurre con lentes macro",
      ],
      correct: 2,
      explain: "La compresion de perspectiva la crea la DISTANCIA, no la distancia focal. Un 200mm y un 50mm disparados desde el mismo lugar, luego recortados al mismo encuadre, se ven identicos. El trabajo del teleobjetivo es permitirte estar lejos manteniendo al sujeto encuadrado — y estar lejos es lo que hace que las montañas se vean grandes y apiladas detras.",
    },
  ],
};

const uiText = {
  en: {
    title: "Photography Knowledge Quiz",
    desc: "Test what you've learned — 17 questions covering exposure, composition, technique, and gear care.",
    start: "Start Quiz",
    next: "Next Question",
    finish: "See Results",
    restart: "Try Again",
    question: "Question",
    of: "of",
    score: "Your Score",
    perfect: "Perfect score! You've mastered the fundamentals!",
    great: "Great job! You know your photography well.",
    good: "Good effort! Review the fundamentals and try again.",
    keep: "Keep studying! The fundamentals section above will help.",
    correct_label: "Correct!",
    wrong_label: "Not quite.",
  },
  es: {
    title: "Quiz de Conocimiento Fotografico",
    desc: "Pon a prueba lo aprendido — 17 preguntas sobre exposicion, composicion, tecnica y cuidado de equipo.",
    start: "Comenzar Quiz",
    next: "Siguiente Pregunta",
    finish: "Ver Resultados",
    restart: "Intentar de Nuevo",
    question: "Pregunta",
    of: "de",
    score: "Tu Puntuacion",
    perfect: "Puntuacion perfecta! Dominaste los fundamentos!",
    great: "Excelente! Conoces bien tu fotografia.",
    good: "Buen esfuerzo! Repasa los fundamentos e intenta de nuevo.",
    keep: "Sigue estudiando! La seccion de fundamentos arriba te ayudara.",
    correct_label: "Correcto!",
    wrong_label: "No exactamente.",
  },
};

export default function PhotoQuiz() {
  const { lang } = useTranslation();
  const ui = uiText[lang] || uiText.en;
  const qs = questions[lang] || questions.en;

  const [started, setStarted] = useState(false);
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);

  const handleSelect = useCallback(
    (idx) => {
      if (answered) return;
      setSelected(idx);
      setAnswered(true);
      if (idx === qs[current].correct) setScore((s) => s + 1);
    },
    [answered, current, qs]
  );

  const handleNext = () => {
    if (current + 1 >= qs.length) {
      setDone(true);
    } else {
      setCurrent((c) => c + 1);
      setSelected(null);
      setAnswered(false);
    }
  };

  const restart = () => {
    setStarted(false);
    setCurrent(0);
    setSelected(null);
    setAnswered(false);
    setScore(0);
    setDone(false);
  };

  const pct = qs.length ? Math.round((score / qs.length) * 100) : 0;
  const msg = pct === 100 ? ui.perfect : pct >= 75 ? ui.great : pct >= 50 ? ui.good : ui.keep;

  return (
    <motion.div
      variants={item}
      className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6"
      data-testid="photo-quiz"
    >
      <div className="flex items-center gap-3 mb-4">
        <Trophy weight="fill" className="w-6 h-6 text-[#F5A524]" />
        <div>
          <h3 className="font-['Outfit'] text-lg text-white font-medium">{ui.title}</h3>
          <p className="text-[#71717A] text-sm">{ui.desc}</p>
        </div>
      </div>

      {!started ? (
        <div className="text-center py-6">
          <Button
            onClick={() => setStarted(true)}
            className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium px-8"
            data-testid="quiz-start-btn"
          >
            {ui.start}
          </Button>
        </div>
      ) : done ? (
        <div className="text-center py-6 space-y-4" data-testid="quiz-results">
          <div className="text-[#71717A] text-xs uppercase tracking-widest">{ui.score}</div>
          <div className="font-['JetBrains_Mono'] text-5xl text-[#F5A524] setting-value-glow">
            {score}/{qs.length}
          </div>
          <div className="w-full bg-[#0A0A0A] rounded-full h-2 overflow-hidden">
            <div
              className="h-full bg-[#F5A524] rounded-full transition-all duration-700"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="text-[#A1A1AA] text-sm">{msg}</p>
          <Button
            variant="outline"
            onClick={restart}
            className="border-white/10 text-white hover:bg-white/5 mt-2"
            data-testid="quiz-restart-btn"
          >
            <ArrowCounterClockwise className="w-4 h-4 mr-1.5" />
            {ui.restart}
          </Button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="quiz-question-area">
          <div className="flex items-center justify-between">
            <span className="text-[#71717A] text-sm">
              {ui.question} {current + 1} {ui.of} {qs.length}
            </span>
            <span className="text-[#F5A524] text-sm font-['JetBrains_Mono']">{score} pts</span>
          </div>
          <div className="w-full bg-[#0A0A0A] rounded-full h-1.5 overflow-hidden">
            <div
              className="h-full bg-[#F5A524]/40 rounded-full transition-all"
              style={{ width: `${((current + 1) / qs.length) * 100}%` }}
            />
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.25 }}
              className="space-y-3"
            >
              <p className="text-white font-medium" data-testid="quiz-question-text">{qs[current].q}</p>
              <div className="grid gap-2">
                {qs[current].opts.map((opt, i) => {
                  let cls = "bg-[#0A0A0A] border border-white/10 text-[#A1A1AA] hover:border-white/20";
                  if (answered) {
                    if (i === qs[current].correct) cls = "bg-green-900/30 border border-green-500/50 text-green-300";
                    else if (i === selected && i !== qs[current].correct)
                      cls = "bg-red-900/30 border border-red-500/50 text-red-300";
                    else cls = "bg-[#0A0A0A] border border-white/5 text-[#52525B]";
                  }
                  return (
                    <button
                      key={i}
                      onClick={() => handleSelect(i)}
                      disabled={answered}
                      className={`w-full text-left rounded-lg p-3 text-sm transition-colors ${cls}`}
                      data-testid={`quiz-opt-${i}`}
                    >
                      <span className="font-['JetBrains_Mono'] mr-2 text-xs">{String.fromCharCode(65 + i)}.</span>
                      {opt}
                    </button>
                  );
                })}
              </div>
              {answered && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3 space-y-1"
                  data-testid="quiz-explanation"
                >
                  <span
                    className={`text-xs font-semibold uppercase ${
                      selected === qs[current].correct ? "text-green-400" : "text-red-400"
                    }`}
                  >
                    {selected === qs[current].correct ? ui.correct_label : ui.wrong_label}
                  </span>
                  <p className="text-[#A1A1AA] text-sm">{qs[current].explain}</p>
                </motion.div>
              )}
            </motion.div>
          </AnimatePresence>

          {answered && (
            <div className="flex justify-end">
              <Button
                onClick={handleNext}
                className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium"
                data-testid="quiz-next-btn"
              >
                {current + 1 >= qs.length ? ui.finish : ui.next}
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}
